// PolicyModals.js
import React, { useState, useEffect, useRef } from "react";
import { Card } from "../../components/ui/card";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Lock, UserCheck, Database, Eye, Shield,
  FileText, Scale, X, Loader2, ChevronRight, Globe
} from "lucide-react";
import { useContentManagement } from "../../modules/shared/hooks/useContentManagement";
import { ContentManagementTypeEnum } from "../../modules/shared/model/ContentManagementInterfaces";
import { useLanguageContext } from "../../contexts/LanguageContext";
import { HttpService } from "../../modules/shared/services/http.service";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { getAllLanguages } from "../../redux/Actions/LanguageAction";

// Base Modal Component
const Modal = ({ isOpen, onClose, children }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black z-[60]"
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-2 sm:inset-4 md:inset-10 z-[70] bg-white rounded-xl shadow-2xl overflow-hidden"
        >
          {children}
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// Accordion Component
const AccordionItem = ({ title, children, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <motion.div
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronRight className="h-5 w-5 text-gray-500" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Helper function to check if language is not English
const isNonEnglishLanguage = (language) => {
  if (!language) return false;
  const langCode = (language.initial || language.name || "").toLowerCase();
  const langName = (language.name || "").toLowerCase();
  return langCode !== "en" && langName !== "english" && langCode !== "";
};

// Helper function to get language object from backend using i18n language code
const getLanguageFromCode = (langCode: string, languages: any[]) => {
  if (!langCode || !languages || languages.length === 0) return null;
  
  // Extract just the language code (e.g., "it-IT" -> "it")
  const code = langCode.split("-")[0].toLowerCase();
  
  // Find language by initial code
  return languages.find((lang: any) => 
    lang.initial?.toLowerCase() === code
  ) || null;
};

// Privacy Policy Content with Dynamic Content Management Data
const PrivacyContent = ({ onClose }) => {
  console.log("🟠 [Policy.tsx - PrivacyContent] Component rendering");
  const { getContentByType, loading } = useContentManagement();
  const { currentLanguage } = useLanguageContext();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [backendLanguages, setBackendLanguages] = useState([]);
  const [contentSections, setContentSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const hasFetched = React.useRef(false);

  // Log component lifecycle
  useEffect(() => {
    console.log("🟠 [Policy.tsx - PrivacyContent] Component mounted");
    return () => {
      console.log("🟠 [Policy.tsx - PrivacyContent] Component unmounting");
    };
  }, []);

  // Fetch backend languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const result = await dispatch(getAllLanguages({
          page: 1,
          limit: 50,
          isShowLoader: false
        })).unwrap();
        
        const languagesData = result?.data || result || [];
        setBackendLanguages(languagesData);
        console.log("[PolicyModals - Privacy] Backend languages loaded:", languagesData);
      } catch (error) {
        console.error("[PolicyModals - Privacy] Failed to load languages:", error);
      }
    };

    fetchLanguages();
  }, [dispatch]);

  useEffect(() => {
    hasFetched.current = false; // Reset on language change
  }, [currentLanguage, i18n.language]);

  useEffect(() => {
    console.log("🟠 [Policy.tsx - PrivacyContent] useEffect triggered for content fetch");
    console.log("🟠 [Policy.tsx - PrivacyContent] hasFetched.current:", hasFetched.current);
    console.log("🟠 [Policy.tsx - PrivacyContent] backendLanguages.length:", backendLanguages.length);
    
    // Prevent multiple fetches or fetch before languages are loaded
    if (hasFetched.current || backendLanguages.length === 0) {
      console.log("🟠 [Policy.tsx - PrivacyContent] Skipping fetch - already fetched or languages not loaded");
      return;
    }
    
    const fetchPrivacyContent = async () => {
      try {
        console.log("🟠🟠🟠 [Policy.tsx - PrivacyContent] STARTING DATA FETCH");
        hasFetched.current = true;
        setIsLoading(true);
        setError(null);
        
        console.log("[PolicyModals] Fetching Privacy Policy");
        console.log("[PolicyModals] currentLanguage:", currentLanguage);
        console.log("[PolicyModals] i18n language:", i18n.language);
        
        // Fetch content for Privacy Policy
        const response = await getContentByType(ContentManagementTypeEnum.Privacy_Policy);
        const content = (response as any)?.payload?.data || [];
        
        if (Array.isArray(content)) {
          // Get language from i18n.language using backend languages
          const currentLangCode = i18n.language.split("-")[0].toLowerCase();
          console.log("[PolicyModals - Privacy] Current i18n language code:", currentLangCode);
          console.log("[PolicyModals - Privacy] Backend languages:", backendLanguages);
          
          const languageToUse = backendLanguages.find((lang: any) => 
            lang.initial?.toLowerCase() === currentLangCode
          );
          
          console.log("[PolicyModals - Privacy] Found language by initial:", languageToUse);
          
          // For each section, try to get translation if language is selected and not English
          if (languageToUse && isNonEnglishLanguage(languageToUse)) {
            console.log(`[PolicyModals - Privacy] 🌍 Fetching translations for language ID: ${languageToUse.id}, name: ${languageToUse.name}, initial: ${languageToUse.initial}`);
            
            const sectionsWithTranslations = await Promise.all(
              content.map(async (section) => {
                try {
                  console.log(`[PolicyModals - Privacy] Fetching translations for section ${section.id}...`);
                  // Get translations for this section
                  const translationsResponse = await HttpService.get(
                    `api/v1/content-management-translations/content-management/${section.id}`,
                    true,
                    {}
                  );
                  
                  console.log(`[PolicyModals - Privacy] Translations response for section ${section.id}:`, translationsResponse);
                  
                  if (translationsResponse && Array.isArray(translationsResponse)) {
                    console.log(`[PolicyModals - Privacy] Looking for language ID: ${languageToUse.id}`);
                    
                    const translation = translationsResponse.find(
                      (t: any) => {
                        console.log(`[PolicyModals - Privacy] Checking translation - translation.language.id: ${t.language?.id} === languageToUse.id: ${languageToUse.id} ?`);
                        return t.language?.id === languageToUse.id;
                      }
                    );
                    
                    if (translation) {
                      console.log(`[PolicyModals - Privacy] ✅ Translation found for section: ${section.contentTitle}`, translation);
                      return {
                        ...section,
                        contentTitle: translation.title || section.contentTitle,
                        contentDescription: translation.content || section.contentDescription,
                        _isTranslated: true
                      };
                    } else {
                      console.log(`[PolicyModals - Privacy] ⚠️ No translation found for section: ${section.contentTitle}`);
                    }
                  }
                } catch (err) {
                  console.error(`[PolicyModals - Privacy] ❌ Failed to fetch translation for section ${section.id}:`, err);
                }
                
                return { ...section, _isTranslated: false };
              })
            );
            
            setContentSections(sectionsWithTranslations);
          } else {
            console.log("[PolicyModals - Privacy] Using English content (no language or English selected)");
            setContentSections(content);
          }
        } else {
          setError(t("policyModals.common.noContentFound"));
        }
      } catch (err) {
        console.error("Failed to fetch privacy content:", err);
        setError(t("policyModals.common.failedToLoad", { type: t("policyModals.privacyPolicy.title").toLowerCase() }));
        hasFetched.current = false; // Allow retry on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrivacyContent();
  }, [currentLanguage, i18n.language, t, backendLanguages]); // Re-fetch when language or backend languages change

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const renderContent = (section) => {
    // Check if content starts with HTML tags (even if isText is true)
    const content = section.contentDescription || "";
    const startsWithHtmlTag = /^\s*<[^>]+>/.test(content);
    
    // If isText is false OR content starts with HTML tags, render as HTML
    if (!section.isText || startsWithHtmlTag) {
      return (
        <div>
          <div 
            dangerouslySetInnerHTML={{ __html: content }}
            className="prose prose-lg max-w-none"
          />
        </div>
      );
    }
    
    // Otherwise render as plain text
    return (
      <div>
        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
          {content}
        </p>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header - Left aligned */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center gap-2">
                <img
                  src="https://assests.netlify.app/assets/images/logo.png"
                  alt="Kokobeo Logo"
                  className="h-8 w-auto"
                />
                <span className="text-xl font-bold text-blue-600 text-left">Kokobeo</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex items-center text-gray-500 hover:text-gray-700 p-2 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            {/* Title Section */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center justify-center p-2 bg-blue-100 rounded-full">
                  <Lock className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {t("policyModals.privacyPolicy.title")}
                  </h1>
                  <p className="text-gray-600 mt-1">
                    {t("policyModals.privacyPolicy.lastUpdated", { date: new Date().toLocaleDateString() })}
                  </p>
                </div>
                {/* {currentLanguage && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                    <Globe className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">{currentLanguage.name}</span>
                  </div>
                )} */}
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="text-gray-600">{t("policyModals.common.loadingPrivacy")}</p>
                </div>
              </Card>
            )}

            {/* Error State */}
            {error && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <Lock className="h-8 w-8 text-gray-400" />
                  <p className="text-gray-600">{error}</p>
                  <button
                    onClick={() => {
                      hasFetched.current = false;
                      setIsLoading(true);
                      setError(null);
                      // Trigger re-fetch
                      const fetchPrivacyContent = async () => {
                        try {
                          hasFetched.current = true;
                          const response = await getContentByType(ContentManagementTypeEnum.Privacy_Policy);
                          const content = (response as any)?.payload?.data || [];
                          
                          if (Array.isArray(content)) {
                            setContentSections(content);
                          } else {
                            setError(t("policyModals.common.noContentFound"));
                          }
                        } catch (err) {
                          console.error("Failed to fetch privacy content:", err);
                          setError(t("policyModals.common.failedToLoad", { type: t("policyModals.privacyPolicy.title").toLowerCase() }));
                          hasFetched.current = false;
                        } finally {
                          setIsLoading(false);
                        }
                      };
                      fetchPrivacyContent();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {t("policyModals.common.retry")}
                  </button>
                </div>
              </Card>
            )}

            {/* Content Sections */}
            {!isLoading && !error && contentSections.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {contentSections.map((section, index) => (
                  <AccordionItem
                    key={section.id || index}
                    title={section.contentTitle || `Section ${index + 1}`}
                    isOpen={openSections[section.id || index]}
                    onToggle={() => toggleSection(section.id || index)}
                  >
                    <div className="text-left text-gray-700">
                      {renderContent(section)}
                    </div>
                  </AccordionItem>
                ))}
              </div>
            )}

            {/* No Content State */}
            {!isLoading && !error && contentSections.length === 0 && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <Lock className="h-8 w-8 text-gray-400" />
                  <p className="text-gray-600">{t("policyModals.common.noContentAvailable", { type: t("policyModals.privacyPolicy.title").toLowerCase() })}</p>
                </div>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

// Terms Content Component with Dynamic Content Management Data
const TermsContent = ({ onClose }) => {
  console.log("🟠 [Policy.tsx - TermsContent] Component rendering");
  const { getContentByType, loading } = useContentManagement();
  const { currentLanguage } = useLanguageContext();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [backendLanguages, setBackendLanguages] = useState([]);
  const [contentSections, setContentSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const hasFetched = React.useRef(false);

  // Log component lifecycle
  useEffect(() => {
    console.log("🟠 [Policy.tsx - TermsContent] Component mounted");
    return () => {
      console.log("🟠 [Policy.tsx - TermsContent] Component unmounting");
    };
  }, []);

  // Fetch backend languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const result = await dispatch(getAllLanguages({
          page: 1,
          limit: 50,
          isShowLoader: false
        })).unwrap();
        
        const languagesData = result?.data || result || [];
        setBackendLanguages(languagesData);
        console.log("[PolicyModals - Terms] Backend languages loaded:", languagesData);
      } catch (error) {
        console.error("[PolicyModals - Terms] Failed to load languages:", error);
      }
    };

    fetchLanguages();
  }, [dispatch]);

  useEffect(() => {
    hasFetched.current = false; // Reset on language change
  }, [currentLanguage, i18n.language]);

  useEffect(() => {
    console.log("🟠 [Policy.tsx - TermsContent] useEffect triggered for content fetch");
    console.log("🟠 [Policy.tsx - TermsContent] hasFetched.current:", hasFetched.current);
    console.log("🟠 [Policy.tsx - TermsContent] backendLanguages.length:", backendLanguages.length);
    
    // Prevent multiple fetches or fetch before languages are loaded
    if (hasFetched.current || backendLanguages.length === 0) {
      console.log("🟠 [Policy.tsx - TermsContent] Skipping fetch - already fetched or languages not loaded");
      return;
    }
    
    const fetchTermsContent = async () => {
      try {
        console.log("🟠🟠🟠 [Policy.tsx - TermsContent] STARTING DATA FETCH");
        hasFetched.current = true;
        setIsLoading(true);
        setError(null);
        
        console.log("[PolicyModals] Fetching Terms & Conditions");
        console.log("[PolicyModals] currentLanguage:", currentLanguage);
        console.log("[PolicyModals] i18n language:", i18n.language);
        
        // Fetch content for Terms & Conditions
        const response = await getContentByType(ContentManagementTypeEnum.Terms_Conditions);
        const content = (response as any)?.payload?.data || [];
        
        if (Array.isArray(content)) {
          // Get language from i18n.language using backend languages
          const currentLangCode = i18n.language.split("-")[0].toLowerCase();
          console.log("[PolicyModals - Terms] Current i18n language code:", currentLangCode);
          console.log("[PolicyModals - Terms] Backend languages:", backendLanguages);
          
          const languageToUse = backendLanguages.find((lang: any) => 
            lang.initial?.toLowerCase() === currentLangCode
          );
          
          console.log("[PolicyModals - Terms] Found language by initial:", languageToUse);
          
          // For each section, try to get translation if language is selected and not English
          if (languageToUse && isNonEnglishLanguage(languageToUse)) {
            console.log(`[PolicyModals - Terms] 🌍 Fetching translations for language ID: ${languageToUse.id}, name: ${languageToUse.name}, initial: ${languageToUse.initial}`);
            
            const sectionsWithTranslations = await Promise.all(
              content.map(async (section) => {
                try {
                  console.log(`[PolicyModals - Terms] Fetching translations for section ${section.id}...`);
                  // Get translations for this section
                  const translationsResponse = await HttpService.get(
                    `api/v1/content-management-translations/content-management/${section.id}`,
                    true,
                    {}
                  );
                  
                  console.log(`[PolicyModals - Terms] Translations response for section ${section.id}:`, translationsResponse);
                  
                  if (translationsResponse && Array.isArray(translationsResponse)) {
                    console.log(`[PolicyModals - Terms] Looking for language ID: ${languageToUse.id}`);
                    
                    const translation = translationsResponse.find(
                      (t: any) => {
                        console.log(`[PolicyModals - Terms] Checking translation - translation.language.id: ${t.language?.id} === languageToUse.id: ${languageToUse.id} ?`);
                        return t.language?.id === languageToUse.id;
                      }
                    );
                    
                    if (translation) {
                      console.log(`[PolicyModals - Terms] ✅ Translation found for section: ${section.contentTitle}`, translation);
                      return {
                        ...section,
                        contentTitle: translation.title || section.contentTitle,
                        contentDescription: translation.content || section.contentDescription,
                        _isTranslated: true
                      };
                    } else {
                      console.log(`[PolicyModals - Terms] ⚠️ No translation found for section: ${section.contentTitle}`);
                    }
                  }
                } catch (err) {
                  console.error(`[PolicyModals - Terms] ❌ Failed to fetch translation for section ${section.id}:`, err);
                }
                
                return { ...section, _isTranslated: false };
              })
            );
            
            setContentSections(sectionsWithTranslations);
          } else {
            console.log("[PolicyModals - Terms] Using English content (no language or English selected)");
            setContentSections(content);
          }
        } else {
          setError(t("policyModals.common.noContentFound"));
        }
      } catch (err) {
        console.error("Failed to fetch terms content:", err);
        setError(t("policyModals.common.failedToLoad", { type: t("policyModals.termsOfService.title").toLowerCase() }));
        hasFetched.current = false; // Allow retry on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchTermsContent();
  }, [currentLanguage, i18n.language, t, backendLanguages]); // Re-fetch when language or backend languages change

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const renderContent = (section) => {
    // Check if content starts with HTML tags (even if isText is true)
    const content = section.contentDescription || "";
    const startsWithHtmlTag = /^\s*<[^>]+>/.test(content);
    
    // If isText is false OR content starts with HTML tags, render as HTML
    if (!section.isText || startsWithHtmlTag) {
      return (
        <div>
          <div 
            dangerouslySetInnerHTML={{ __html: content }}
            className="prose prose-lg max-w-none"
          />
        </div>
      );
    }
    
    // Otherwise render as plain text
    return (
      <div>
        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
          {content}
        </p>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center gap-2">
                <img
                  src="https://assests.netlify.app/assets/images/logo.png"
                  alt="Kokobeo Logo"
                  className="h-8 w-auto"
                />
                <span className="text-xl font-bold text-blue-600">Kokobeo</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* {currentLanguage && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                  <Globe className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">{currentLanguage.name}</span>
                </div>
              )} */}
              <button
                onClick={onClose}
                className="flex items-center text-gray-500 hover:text-gray-700 p-2 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            {/* Title Section */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center justify-center p-2 bg-blue-100 rounded-full">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {t("policyModals.termsOfService.title")}
                  </h1>
                  <p className="text-gray-600 mt-1">
                    {t("policyModals.termsOfService.lastUpdated", { date: new Date().toLocaleDateString() })}
                  </p>
                </div>
                {/* {currentLanguage && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                    <Globe className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">{currentLanguage.name}</span>
                  </div>
                )} */}
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="text-gray-600">{t("policyModals.common.loadingTerms")}</p>
                </div>
              </Card>
            )}

            {/* Error State */}
            {error && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <FileText className="h-8 w-8 text-gray-400" />
                  <p className="text-gray-600">{error}</p>
                  <button
                    onClick={() => {
                      hasFetched.current = false;
                      setIsLoading(true);
                      setError(null);
                      // Trigger re-fetch by calling the effect again
                      const fetchTermsContent = async () => {
                        try {
                          hasFetched.current = true;
                          const response = await getContentByType(ContentManagementTypeEnum.Terms_Conditions);
                          const content = (response as any)?.payload?.data || [];
                          
                          if (Array.isArray(content)) {
                            setContentSections(content);
                          } else {
                            setError(t("policyModals.common.noContentFound"));
                          }
                        } catch (err) {
                          console.error("Failed to fetch terms content:", err);
                          setError(t("policyModals.common.failedToLoad", { type: t("policyModals.termsOfService.title").toLowerCase() }));
                          hasFetched.current = false;
                        } finally {
                          setIsLoading(false);
                        }
                      };
                      fetchTermsContent();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {t("policyModals.common.retry")}
                  </button>
                </div>
              </Card>
            )}

            {/* Content Sections */}
            {!isLoading && !error && contentSections.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {contentSections.map((section, index) => (
                  <AccordionItem
                    key={section.id || index}
                    title={section.contentTitle || `Section ${index + 1}`}
                    isOpen={openSections[section.id || index]}
                    onToggle={() => toggleSection(section.id || index)}
                  >
                    <div className="text-left text-gray-700">
                      {renderContent(section)}
                    </div>
                  </AccordionItem>
                ))}
              </div>
            )}

            {/* No Content State */}
            {!isLoading && !error && contentSections.length === 0 && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <FileText className="h-8 w-8 text-gray-400" />
                  <p className="text-gray-600">{t("policyModals.common.noContentAvailable", { type: t("policyModals.termsOfService.title").toLowerCase() })}</p>
                </div>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

// Professional Terms Content Component with Dynamic Content Management Data
const ProfessionalTermsContent = ({ onClose }) => {
  const { getContentByType, loading } = useContentManagement();
  const { currentLanguage } = useLanguageContext();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [backendLanguages, setBackendLanguages] = useState([]);
  const [contentSections, setContentSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const hasFetched = React.useRef(false);

  // Fetch backend languages on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const result = await dispatch(getAllLanguages({
          page: 1,
          limit: 50,
          isShowLoader: false
        })).unwrap();
        
        const languagesData = result?.data || result || [];
        setBackendLanguages(languagesData);
        console.log("[PolicyModals - Professional] Backend languages loaded:", languagesData);
      } catch (error) {
        console.error("[PolicyModals - Professional] Failed to load languages:", error);
      }
    };

    fetchLanguages();
  }, [dispatch]);

  useEffect(() => {
    hasFetched.current = false; // Reset on language change
  }, [currentLanguage, i18n.language]);

  useEffect(() => {
    // Prevent multiple fetches or fetch before languages are loaded
    if (hasFetched.current || backendLanguages.length === 0) return;
    
    const fetchProfessionalTermsContent = async () => {
      try {
        hasFetched.current = true;
        setIsLoading(true);
        setError(null);
        
        console.log("[PolicyModals] Fetching Professional Terms");
        console.log("[PolicyModals] currentLanguage:", currentLanguage);
        console.log("[PolicyModals] i18n language:", i18n.language);
        
        // Fetch content for Professional Terms
        const response = await getContentByType(ContentManagementTypeEnum.Professional_Terms);
        const content = (response as any)?.payload?.data || [];
        
        if (Array.isArray(content)) {
          // Get language from i18n.language using backend languages
          const currentLangCode = i18n.language.split("-")[0].toLowerCase();
          console.log("[PolicyModals - Professional] Current i18n language code:", currentLangCode);
          console.log("[PolicyModals - Professional] Backend languages:", backendLanguages);
          
          const languageToUse = backendLanguages.find((lang: any) => 
            lang.initial?.toLowerCase() === currentLangCode
          );
          
          console.log("[PolicyModals - Professional] Found language by initial:", languageToUse);
          
          // For each section, try to get translation if language is selected and not English
          if (languageToUse && isNonEnglishLanguage(languageToUse)) {
            console.log(`[PolicyModals - Professional] 🌍 Fetching translations for language ID: ${languageToUse.id}, name: ${languageToUse.name}, initial: ${languageToUse.initial}`);
            
            const sectionsWithTranslations = await Promise.all(
              content.map(async (section) => {
                try {
                  console.log(`[PolicyModals - Professional] Fetching translations for section ${section.id}...`);
                  // Get translations for this section
                  const translationsResponse = await HttpService.get(
                    `api/v1/content-management-translations/content-management/${section.id}`,
                    true,
                    {}
                  );
                  
                  console.log(`[PolicyModals - Professional] Translations response for section ${section.id}:`, translationsResponse);
                  
                  if (translationsResponse && Array.isArray(translationsResponse)) {
                    console.log(`[PolicyModals - Professional] Looking for language ID: ${languageToUse.id}`);
                    
                    const translation = translationsResponse.find(
                      (t: any) => {
                        console.log(`[PolicyModals - Professional] Checking translation - translation.language.id: ${t.language?.id} === languageToUse.id: ${languageToUse.id} ?`);
                        return t.language?.id === languageToUse.id;
                      }
                    );
                    
                    if (translation) {
                      console.log(`[PolicyModals - Professional] ✅ Translation found for section: ${section.contentTitle}`, translation);
                      return {
                        ...section,
                        contentTitle: translation.title || section.contentTitle,
                        contentDescription: translation.content || section.contentDescription,
                        _isTranslated: true
                      };
                    } else {
                      console.log(`[PolicyModals - Professional] ⚠️ No translation found for section: ${section.contentTitle}`);
                    }
                  }
                } catch (err) {
                  console.error(`[PolicyModals - Professional] ❌ Failed to fetch translation for section ${section.id}:`, err);
                }
                
                return { ...section, _isTranslated: false };
              })
            );
            
            setContentSections(sectionsWithTranslations);
          } else {
            console.log("[PolicyModals - Professional] Using English content (no language or English selected)");
            setContentSections(content);
          }
        } else {
          setError(t("policyModals.common.noContentFound"));
        }
      } catch (err) {
        console.error("Failed to fetch professional terms content:", err);
        setError(t("policyModals.common.failedToLoad", { type: t("policyModals.professionalTerms.title").toLowerCase() }));
        hasFetched.current = false; // Allow retry on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfessionalTermsContent();
  }, [currentLanguage, i18n.language, t, backendLanguages]); // Re-fetch when language or backend languages change

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const renderContent = (section) => {
    // Check if content starts with HTML tags (even if isText is true)
    const content = section.contentDescription || "";
    const startsWithHtmlTag = /^\s*<[^>]+>/.test(content);
    
    // If isText is false OR content starts with HTML tags, render as HTML
    if (!section.isText || startsWithHtmlTag) {
      return (
        <div>
          <div 
            dangerouslySetInnerHTML={{ __html: content }}
            className="prose prose-lg max-w-none"
          />
        </div>
      );
    }
    
    // Otherwise render as plain text
    return (
      <div>
        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
          {content}
        </p>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="w-full px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center gap-2">
                <img
                  src="https://assests.netlify.app/assets/images/logo.png"
                  alt="Kokobeo Logo"
                  className="h-8 w-auto"
                />
                <span className="text-xl font-bold text-blue-600">Kokobeo</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* {currentLanguage && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                  <Globe className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">{currentLanguage.name}</span>
                </div>
              )} */}
              <button
                onClick={onClose}
                className="flex items-center text-gray-500 hover:text-gray-700 p-2 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            {/* Title Section */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center justify-center p-2 bg-blue-100 rounded-full">
                  <Scale className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {t("policyModals.professionalTerms.title")}
                  </h1>
                  <p className="text-gray-600 mt-1">
                    {t("policyModals.professionalTerms.lastUpdated", { date: new Date().toLocaleDateString() })}
                  </p>
                </div>
                {/* {currentLanguage && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
                    <Globe className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">{currentLanguage.name}</span>
                  </div>
                )} */}
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <p className="text-gray-600">{t("policyModals.common.loadingProfessional")}</p>
                </div>
              </Card>
            )}

            {/* Error State */}
            {error && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <FileText className="h-8 w-8 text-gray-400" />
                  <p className="text-gray-600">{error}</p>
                  <button
                    onClick={() => {
                      hasFetched.current = false;
                      setIsLoading(true);
                      setError(null);
                      // Trigger re-fetch
                      const fetchProfessionalTermsContent = async () => {
                        try {
                          hasFetched.current = true;
                          const response = await getContentByType(ContentManagementTypeEnum.Professional_Terms);
                          const content = (response as any)?.payload?.data || [];
                          
                          if (Array.isArray(content)) {
                            setContentSections(content);
                          } else {
                            setError(t("policyModals.common.noContentFound"));
                          }
                        } catch (err) {
                          console.error("Failed to fetch professional terms content:", err);
                          setError(t("policyModals.common.failedToLoad", { type: t("policyModals.professionalTerms.title").toLowerCase() }));
                          hasFetched.current = false;
                        } finally {
                          setIsLoading(false);
                        }
                      };
                      fetchProfessionalTermsContent();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {t("policyModals.common.retry")}
                  </button>
                </div>
              </Card>
            )}

            {/* Content Sections */}
            {!isLoading && !error && contentSections.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {contentSections.map((section, index) => (
                  <AccordionItem
                    key={section.id || index}
                    title={section.contentTitle || `Section ${index + 1}`}
                    isOpen={openSections[section.id || index]}
                    onToggle={() => toggleSection(section.id || index)}
                  >
                    <div className="text-left text-gray-700">
                      {renderContent(section)}
                    </div>
                  </AccordionItem>
                ))}
              </div>
            )}

            {/* No Content State */}
            {!isLoading && !error && contentSections.length === 0 && (
              <Card className="p-8 bg-white text-center">
                <div className="flex flex-col items-center space-y-4">
                  <FileText className="h-8 w-8 text-gray-400" />
                  <p className="text-gray-600">{t("policyModals.common.noContentAvailable", { type: t("policyModals.professionalTerms.title").toLowerCase() })}</p>
                </div>
              </Card>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

// Main PolicyModals Component
const PolicyModals = ({ showPrivacyModal, showTermsModal, showProfessionalTermsModal, onClosePrivacy, onCloseTerms, onCloseProfessionalTerms }) => {
  return (
    <>
      <Modal isOpen={showPrivacyModal} onClose={onClosePrivacy}>
        <PrivacyContent onClose={onClosePrivacy} />
      </Modal>

      <Modal isOpen={showTermsModal} onClose={onCloseTerms}>
        <TermsContent onClose={onCloseTerms} />
      </Modal>

      <Modal isOpen={showProfessionalTermsModal} onClose={onCloseProfessionalTerms}>
        <ProfessionalTermsContent onClose={onCloseProfessionalTerms} />
      </Modal>
    </>
  );
};

export default PolicyModals;